<script setup lang="ts">
/**
 * Mobile slide-in menu: a full-viewport position:fixed element that is itself the
 * scroller, with a sticky chrome, rendering the shared MobileMenuOverlay content.
 *
 * iOS scroll gotcha: this MUST stay above the Voyada SDK's #voyada-drawer (see the
 * z-index note in the styles) or its touch scrolling silently breaks on iOS.
 */
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import CurrencySelector from '@/components/CurrencySelector.vue'
import MobileMenuOverlay from '@/components/MobileMenuOverlay.vue'
import { useHomepagePhoneContacts } from '@/composables/homepage'
import { showMobileAccountSheet } from '@/composables/useMobileMenu'
import { useUserStore } from '@/stores/UserStore'
import { dataUrl } from '@/utils/AssetUtils'

defineProps<{ open: boolean }>()
const emit = defineEmits<{ close: [] }>()

const { t } = useI18n()
const { currentContact } = useHomepagePhoneContacts()
const userStore = useUserStore()

const userAvatar = computed(() => {
  const a = String(userStore.user.avatar || '').trim()
  return a && a !== '/images/icon_user.png' ? dataUrl(a) : ''
})

function openLogin() {
  // Close the menu, then trigger the shared login dialog (driven by
  // userStore.showLoginDlg, the same flow used by the desktop header and the
  // detail / booking pages).
  emit('close')
  userStore.requestLogin()
}

function openAccount() {
  emit('close')
  showMobileAccountSheet.value = true
}
</script>

<template>
  <div v-if="open" class="mm-menu">
    <header class="mm-menu__chrome">
      <div class="mm-top">
        <a href="/" class="mm-logo">
          <img src="/images/logo_g.png" alt="TripToGo" />
        </a>
        <div class="mm-top-right">
          <div class="mm-lang">
            <CurrencySelector />
          </div>
          <button
            v-if="!userStore.user.isLogin"
            class="mm-login"
            type="button"
            @click="openLogin"
          >
            <i class="mm-login-icon"><img src="/images/icon_user_b.png" alt="" /></i>
            <span>{{ t('common.login') }}</span>
          </button>
          <button
            v-else
            class="mm-login mm-login--account"
            type="button"
            @click="openAccount"
          >
            <img v-if="userAvatar" class="mm-login-avatar" :src="userAvatar" alt="" />
            <i v-else class="mm-login-icon"><img src="/images/icon_user_b.png" alt="" /></i>
            <span>{{ t('mobileNav.account') }}</span>
          </button>
          <button class="mm-close" type="button" aria-label="Close menu" @click="emit('close')">
            <ITablerX class="mm-close-icon" />
          </button>
        </div>
      </div>

      <div class="mm-auth">
        <div class="mm-booknow">
          <img src="/images/home/phone-icon.svg" class="mm-phone-icon" alt="" />
          <span>Book Now</span>
        </div>
        <div class="mm-contacts">
          <span class="mm-contact">
            <span>{{ currentContact.region }}:</span>
            <span>{{ currentContact.number }}</span>
          </span>
        </div>
      </div>
    </header>

    <div class="mm-body">
      <MobileMenuOverlay />
    </div>
  </div>
</template>

<style scoped>
/* ===== Scroll shell: the fixed element is itself the scroller ===== */
.mm-menu {
  position: fixed;
  inset: 0;
  /* MUST be above the Voyada SDK's #voyada-drawer — a full-viewport position:fixed <body>
     child at z-index:9999, appended after #app. At a tied z-index the drawer paints on top
     (equal z-index resolves by DOM order) and swallows touch on iOS, silently freezing this
     menu's scroll. This single line was the root cause of the iOS scroll bug. */
  z-index: 100000;
  background: #ffffff;
  color: #333333;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  overflow-y: auto;
  /* Keep the menu's scroll from chaining to the (unlocked) page behind on iOS. */
  overscroll-behavior: contain;
}

.mm-menu__chrome {
  position: sticky;
  top: 0;
  z-index: 1;
  background: #ffffff;
  padding: 14px 24px 0;
}

.mm-body {
  /* Plain flow — the .mm-menu element scrolls. Bottom pad = breathing room + iOS safe area.
     No nav-height reservation: the menu sits above the bottom nav (z-index), so it's covered. */
  padding: 0 24px calc(32px + env(safe-area-inset-bottom));
}

/* ===== Chrome visuals ===== */
.mm-top {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 16px;
}

.mm-logo {
  display: block;
  width: 135px;
}
.mm-logo img {
  width: 100%;
  height: auto;
  display: block;
}

.mm-top-right {
  display: flex;
  align-items: center;
  gap: 12px;
}

.mm-lang {
  display: flex;
  align-items: center;
  color: #111;
}
/* The menu background is white, so use a dark outline for the currency pill. */
.mm-lang :deep(.currency-selector) {
  border-color: rgba(0, 0, 0, 0.3);
  color: #111;
}

/* Login pill — matches the USD pill (.mm-lang) and the desktop header Login button */
.mm-login {
  display: flex;
  align-items: center;
  gap: 6px;
  border: 1px solid rgba(0, 0, 0, 0.3);
  border-radius: 20px;
  padding: 0 14px;
  height: 36px;
  box-sizing: border-box;
  font-size: 14px;
  font-weight: 700;
  font-family: inherit;
  color: inherit;
  background: none;
  cursor: pointer;
  white-space: nowrap;
}
.mm-login-icon img {
  width: 20px;
  height: 20px;
  display: block;
}

/* Logged-in variant: avatar + name, opens the account sheet. Name truncates so
   the chrome row never overflows on small screens. */
.mm-login-avatar {
  width: 20px;
  height: 20px;
  border-radius: 50%;
  object-fit: cover;
  display: block;
  flex-shrink: 0;
}
.mm-login--account {
  max-width: 150px;
}
.mm-login--account span {
  overflow: hidden;
  text-overflow: ellipsis;
}

.mm-close {
  background: none;
  border: none;
  cursor: pointer;
  color: inherit;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 4px;
}
.mm-close-icon {
  width: 24px;
  height: 24px;
}

.mm-auth {
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: 0 0 12px;
  border-bottom: 1px solid rgba(0, 0, 0, 0.15);
  margin-bottom: 8px;
}

.mm-booknow {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  font-weight: 700;
}
.mm-phone-icon {
  width: 14px;
  height: 14px;
  object-fit: contain;
}

.mm-contacts {
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
  font-size: 14px;
  font-weight: 700;
}
.mm-contact {
  display: flex;
  align-items: center;
  gap: 0.4rem;
  white-space: nowrap;
}
.mm-sep {
  opacity: 0.4;
  font-weight: 400;
}
</style>
